import { useRef } from 'react';

export const tabId = (prefix, id) => `${prefix}-tab-${id}`;

/** Props for the tab panel the caller renders: pairs with the tab of the same id. */
export const panelProps = (prefix, id) => ({
  id: `${prefix}-panel-${id}`,
  role: 'tabpanel',
  'aria-labelledby': tabId(prefix, id),
  tabIndex: 0,
});

/**
 * WAI-ARIA tabs. Controlled: the caller owns `value` (from a route or from
 * useTabParam) and renders the panel. Arrow keys, Home and End move and
 * activate (automatic activation, the panels are cheap).
 */
export default function ConsoleTabs({ label, items, value, onChange, idPrefix }) {
  const refs = useRef({});

  function onKeyDown(e) {
    const i = items.findIndex((t) => t.id === value);
    let next = null;
    if (e.key === 'ArrowRight') next = (i + 1) % items.length;
    else if (e.key === 'ArrowLeft') next = (i - 1 + items.length) % items.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = items.length - 1;
    if (next === null) return;
    e.preventDefault();
    onChange(items[next].id);
    refs.current[items[next].id]?.focus();
  }

  return (
    <div role="tablist" aria-label={label} onKeyDown={onKeyDown} className="flex gap-1 overflow-x-auto shadow-[inset_0_-1px_0_theme(colors.ipc.border)]">
      {items.map((t) => {
        const selected = t.id === value;
        return (
          <button
            key={t.id}
            ref={(el) => { refs.current[t.id] = el; }}
            id={tabId(idPrefix, t.id)}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls={`${idPrefix}-panel-${t.id}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(t.id)}
            className={`tap shrink-0 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ipc-light sm:px-4 ${selected ? 'border-ipc-light text-ipc-primary' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
          >
            {t.label}
            {t.count != null ? <span className="badge-slate ml-2">{t.count}</span> : null}
          </button>
        );
      })}
    </div>
  );
}
